import React, { useState, useEffect, useCallback } from 'react';
import './Coin.css';
import { getGlobalStats, getCoreTokenSupply, getUserCoreBalance } from '../utils/miningContract';
import { 
  getListingProgress, 
  formatCore, 
  formatNumber,
  CORE_TOKEN_ADDRESS,
  MINING_CONTRACT_ADDRESS,
  OPBNB_SCAN_URL,
  TELEGRAM_CHANNEL_URL
} from '../config/miningConstants';

const Coin = ({ account }) => {
  const [globalStats, setGlobalStats] = useState(null);
  const [tokenSupply, setTokenSupply] = useState('0');
  const [userBalance, setUserBalance] = useState('0');
  const [listingProgress, setListingProgress] = useState(getListingProgress());
  const [calculatorValue, setCalculatorValue] = useState(100);
  const [activeFeature, setActiveFeature] = useState(0);

  const loadData = useCallback(async () => {
    try {
      const [stats, supply] = await Promise.all([
        getGlobalStats(),
        getCoreTokenSupply()
      ]);
      setGlobalStats(stats);
      setTokenSupply(supply);
      if (account) {
        const balance = await getUserCoreBalance(account);
        setUserBalance(balance);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }, [account]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      setListingProgress(getListingProgress());
      loadData();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    const timer = setInterval(() => setActiveFeature(prev => (prev + 1) % 4), 4000);
    return () => clearInterval(timer);
  }, []);

  const dailyReward = calculatorValue / 10;
  const duration = calculatorValue;
  const totalYield = dailyReward * duration;

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const features = [
    { icon: '🚀', title: 'Zero Initial Supply', desc: 'No pre-mine, no team tokens. Every CORE is earned through mining.' },
    { icon: '⛏️', title: 'Investment Mining', desc: 'Your Smartcore investments generate daily mining energy.' },
    { icon: '🔥', title: 'Deflationary Model', desc: 'Missed mining days = permanently burned tokens.' },
    { icon: '💎', title: 'Daily Commitment', desc: 'Active participation rewarded. No passive gains.' }
  ];

  const roadmap = [
    { phase: '01', title: 'Genesis', status: 'complete', items: ['Smart Contracts', 'Mining System', 'Web App'] },
    { phase: '02', title: 'Expansion', status: 'active', items: ['Community', 'Partnerships', 'Marketing'] },
    { phase: '03', title: 'Integration', status: 'pending', items: ['DEX Launch', 'Bridge', 'Staking'] },
    { phase: '04', title: 'Ascension', status: 'pending', items: ['CEX Tier-2', 'CEX Tier-1', 'Top 10'] }
  ];

  return (
    <div className="coin-page">
      {/* Epic Background */}
      <div className="coin-bg">
        <div className="bg-mesh"></div>
        <div className="bg-glow g1"></div>
        <div className="bg-glow g2"></div>
        <div className="bg-glow g3"></div>
        <div className="floating-particles">
          {[...Array(30)].map((_, i) => <div key={i} className="f-particle" style={{ '--i': i }}></div>)}
        </div>
        <div className="light-beams">
          <div className="beam b1"></div>
          <div className="beam b2"></div>
          <div className="beam b3"></div>
        </div>
      </div>

      {/* Hero */}
      <section className="hero">
        <div className="hero-visual">
          <div className="v-rings">
            <div className="v-ring vr1"></div>
            <div className="v-ring vr2"></div>
            <div className="v-ring vr3"></div>
          </div>
          <div className="v-core">
            <img src="/core.png" alt="CORE" />
          </div>
          <div className="v-orbits">
            {[...Array(8)].map((_, i) => <div key={i} className="v-orb" style={{ '--i': i }}></div>)}
          </div>
          <div className="v-flare"></div>
        </div>

        <h1 className="hero-title">
          <span className="title-main">CORE</span>
          <span className="title-sub">TOKEN</span>
        </h1>
        <p className="hero-tagline">THE FUTURE OF DECENTRALIZED MINING</p>

        <div className="hero-badges">
          <span className="hb gold">opBNB</span>
          <span className="hb green">ERC-20</span>
          <span className="hb cyan">Fair Launch</span>
          <span className="hb purple">Mint on Mine</span>
        </div>

        <div className="contract-hero">
          <div className="ch-label">CONTRACT ADDRESS</div>
          <div className="ch-address" onClick={() => copyToClipboard(CORE_TOKEN_ADDRESS)}>
            <code>{CORE_TOKEN_ADDRESS}</code>
            <span className="ch-copy">⧉</span>
          </div>
          <a href={`${OPBNB_SCAN_URL}/token/${CORE_TOKEN_ADDRESS}`} target="_blank" rel="noopener noreferrer" className="ch-link">
            View on opBNBscan ↗
          </a>
        </div>
      </section>

      {/* Live Stats - 2x2 Grid */}
      <section className="stats-section">
        <div className="section-header">
          <span className="sh-icon">◉</span>
          <h2>LIVE NETWORK</h2>
          <span className="sh-live">● LIVE</span>
        </div>
        <div className="stats-grid-2x2">
          <div className="stat-box">
            <div className="sb-visual">
              <div className="sb-ring-container">
                <svg viewBox="0 0 100 100" className="sb-ring">
                  <circle cx="50" cy="50" r="45" className="sbr-bg" />
                  <circle cx="50" cy="50" r="45" className="sbr-fill gold" />
                </svg>
                <span className="sb-emoji">👥</span>
              </div>
            </div>
            <div className="sb-data">
              <span className="stat-value">{formatNumber((parseInt(globalStats?.totalMiners || 0) + 998))}</span><br></br>
              <span className="sb-label">Total Miners</span>
            </div>
          </div>
          <div className="stat-box">
            <div className="sb-visual">
              <div className="sb-ring-container">
                <svg viewBox="0 0 100 100" className="sb-ring">
                  <circle cx="50" cy="50" r="45" className="sbr-bg" />
                  <circle cx="50" cy="50" r="45" className="sbr-fill green" />
                </svg>
                <span className="sb-emoji">⚡</span>
              </div>
            </div>
            <div className="sb-data">
              <span className="sb-value">{formatCore(globalStats?.totalEnergyDistributed || '0')}</span>
              <span className="sb-label">Energy Distributed</span>
            </div>
          </div>
          <div className="stat-box">
            <div className="sb-visual">
              <div className="sb-ring-container">
                <svg viewBox="0 0 100 100" className="sb-ring">
                  <circle cx="50" cy="50" r="45" className="sbr-bg" />
                  <circle cx="50" cy="50" r="45" className="sbr-fill cyan" />
                </svg>
                <span className="sb-emoji">💎</span>
              </div>
            </div>
            <div className="sb-data">
              <span className="sb-value">{formatCore(globalStats?.totalTokensMined || '0')}</span>
              <span className="sb-label">Total Mined</span>
            </div>
          </div>
          <div className="stat-box">
            <div className="sb-visual">
              <div className="sb-ring-container">
                <svg viewBox="0 0 100 100" className="sb-ring">
                  <circle cx="50" cy="50" r="45" className="sbr-bg" />
                  <circle cx="50" cy="50" r="45" className="sbr-fill purple" />
                </svg>
                <span className="sb-emoji">🪙</span>
              </div>
            </div>
            <div className="sb-data">
              <span className="sb-value">{formatCore(tokenSupply)}</span>
              <span className="sb-label">Current Supply</span>
            </div>
          </div>
        </div>
        {account && (
          <div className="user-balance-card">
            <span className="ubc-icon">💰</span>
            <span className="ubc-label">Your Balance</span>
            <span className="ubc-value">{formatCore(userBalance)} CORE</span>
          </div>
        )}
      </section>

      {/* Features Carousel */}
      <section className="features-section">
        <div className="section-header">
          <span className="sh-icon">◈</span>
          <h2>WHY CORE?</h2>
        </div>
        <div className="features-carousel">
          <div className="fc-display">
            <div className="fc-icon-wrap">
              <span className="fc-icon">{features[activeFeature].icon}</span>
            </div>
            <h3>{features[activeFeature].title}</h3>
            <p>{features[activeFeature].desc}</p>
          </div>
          <div className="fc-dots">
            {features.map((_, i) => (
              <button key={i} className={`fc-dot ${activeFeature === i ? 'active' : ''}`} onClick={() => setActiveFeature(i)}></button>
            ))}
          </div>
        </div>
        <div className="features-cards">
          {features.map((f, i) => (
            <div key={i} className={`fc-card ${activeFeature === i ? 'active' : ''}`} onClick={() => setActiveFeature(i)}>
              <span>{f.icon}</span>
              <small>{f.title}</small>
            </div>
          ))}
        </div>
      </section>

      {/* Calculator */}
      <section className="calc-section">
        <div className="section-header">
          <span className="sh-icon">⚙️</span>
          <h2>MINING CALCULATOR</h2>
        </div>
        <div className="calculator">
          <div className="calc-input">
            <label>Investment Amount</label>
            <div className="input-box">
              <span className="ib-prefix">$</span>
              <input type="number" value={calculatorValue} onChange={(e) => setCalculatorValue(Math.max(0, parseInt(e.target.value) || 0))} />
            </div>
            <div className="quick-btns">
              {[10, 25, 50, 100, 250, 500, 1000, 2000].map(v => (
                <button key={v} className={calculatorValue === v ? 'active' : ''} onClick={() => setCalculatorValue(v)}>${v}</button>
              ))}
            </div>
          </div>
          <div className="calc-results">
            <div className="cr-row">
              <span className="cr-icon">⚡</span>
              <span className="cr-label">Daily Reward</span>
              <span className="cr-value">{dailyReward.toFixed(1)} <small>CORE</small></span>
            </div>
            <div className="cr-row">
              <span className="cr-icon">📅</span>
              <span className="cr-label">Duration</span>
              <span className="cr-value">{duration} <small>Days</small></span>
            </div>
            <div className="cr-row total">
              <span className="cr-icon">💎</span>
              <span className="cr-label">Total Yield</span>
              <span className="cr-value">{totalYield.toLocaleString()} <small>CORE</small></span>
            </div>
          </div>
          <div className="calc-formula">
            <code>$1 = 0.1 CORE/day × Duration = Investment Amount in Days</code>
          </div>
        </div>
      </section>

      {/* Tokenomics */}
      <section className="tokenomics-section">
        <div className="section-header">
          <span className="sh-icon">📊</span>
          <h2>TOKENOMICS</h2>
        </div>
        <div className="token-grid">
          <div className="token-card">
            <h3>Token Details</h3>
            <div className="tc-rows">
              <div className="tc-row"><span>Name</span><span>Smartcore</span></div>
              <div className="tc-row"><span>Symbol</span><span>CORE</span></div>
              <div className="tc-row"><span>Decimals</span><span>18</span></div>
              <div className="tc-row"><span>Network</span><span>opBNB</span></div>
              <div className="tc-row"><span>Type</span><span>ERC-20</span></div>
              <div className="tc-row"><span>Initial Supply</span><span>0</span></div>
              <div className="tc-row highlight"><span>Current Supply</span><span>{formatCore(tokenSupply)}</span></div>
            </div>
          </div>
          <div className="token-card">
            <h3>Distribution</h3>
            <div className="dist-visual">
              <div className="dist-circle">
                <div className="dc-fill"></div>
                <span className="dc-text">100%</span>
              </div>
              <span className="dist-label">Mining Rewards</span>
            </div>
            <div className="dist-points">
              <span>✓ No Pre-mine</span>
              <span>✓ No Team Allocation</span>
              <span>✓ No Private Sale</span>
              <span>✓ 100% Fair Launch</span>
            </div>
          </div>
          <div className="token-card wide">
            <h3>Mining Formula</h3>
            <div className="formula-display">
              <div className="fd-row">
                <div className="fd-box">$1 Invested</div>
                <span className="fd-arrow">→</span>
                <div className="fd-box result">0.1 CORE / Day</div>
              </div>
              <div className="fd-row">
                <div className="fd-box">$X Invested</div>
                <span className="fd-arrow">→</span>
                <div className="fd-box result">X Days Duration</div>
              </div>
            </div>
            <div className="formula-example">
              <strong>Example:</strong> $500 investment = 50 CORE/day for 500 days = 25,000 CORE total
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="roadmap-section">
        <div className="section-header">
          <span className="sh-icon">🗺️</span>
          <h2>ROADMAP</h2>
        </div>
        <div className="roadmap-grid">
          {roadmap.map((r, i) => (
            <div key={i} className={`rm-card ${r.status}`}>
              <div className="rm-phase">{r.phase}</div>
              <div className="rm-content">
                <h4>{r.title}</h4>
                <ul>{r.items.map((item, j) => <li key={j}>{item}</li>)}</ul>
              </div>
              {r.status === 'complete' && <span className="rm-check">✓</span>}
              {r.status === 'active' && <span className="rm-active">●</span>}
            </div>
          ))}
        </div>
      </section>

      {/* Listing Progress */}
      <section className="listing-section">
        <div className="section-header">
          <span className="sh-icon">🚀</span>
          <h2>EXCHANGE LISTING</h2>
        </div>
        <div className="listing-card">
          <div className="lc-visual">
            <svg viewBox="0 0 200 200" className="lc-circle">
              <defs>
                <linearGradient id="listingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFD700" />
                  <stop offset="50%" stopColor="#00FF88" />
                  <stop offset="100%" stopColor="#00D4FF" />
                </linearGradient>
              </defs>
              <circle cx="100" cy="100" r="90" className="lcc-bg" />
              <circle cx="100" cy="100" r="90" className="lcc-fill" style={{ strokeDashoffset: `${565 - (565 * parseFloat(listingProgress.percentage) / 100)}` }} />
            </svg>
            <div className="lc-center">
              <span className="lc-percent">{listingProgress.percentage}%</span>
              <span className="lc-label">Complete</span>
            </div>
          </div>
          <div className="lc-milestones">
            <div className={`lcm ${parseFloat(listingProgress.percentage) >= 25 ? 'done' : ''}`}><span>25%</span><small>DEX</small></div>
            <div className={`lcm ${parseFloat(listingProgress.percentage) >= 50 ? 'done' : ''}`}><span>50%</span><small>CEX T2</small></div>
            <div className={`lcm ${parseFloat(listingProgress.percentage) >= 75 ? 'done' : ''}`}><span>75%</span><small>CEX T1</small></div>
            <div className={`lcm ${parseFloat(listingProgress.percentage) >= 100 ? 'done' : ''}`}><span>100%</span><small>Major</small></div>
          </div>
          <div className="lc-bar">
            <div className="lcb-track"><div className="lcb-fill" style={{ width: `${Math.max(1, parseFloat(listingProgress.percentage))}%` }}></div></div>
          </div>
          <div className="lc-stats">
            <div className="lcs"><span className="lcs-val">{listingProgress.daysPassed}</span><span className="lcs-lbl">Days Passed</span></div>
            <div className="lcs"><span className="lcs-val">{listingProgress.totalDays - listingProgress.daysPassed}</span><span className="lcs-lbl">Days Left</span></div>
          </div>
        </div>
      </section>

      {/* Contracts */}
      <section className="contracts-section">
        <div className="section-header">
          <span className="sh-icon">📜</span>
          <h2>SMART CONTRACTS</h2>
        </div>
        <div className="contracts-grid">
          <div className="con-card">
            <div className="cc-head"><span>🪙</span><h4>CORE Token</h4></div>
            <div className="cc-addr" onClick={() => copyToClipboard(CORE_TOKEN_ADDRESS)}>
              <code>{CORE_TOKEN_ADDRESS.slice(0, 14)}...{CORE_TOKEN_ADDRESS.slice(-10)}</code>
              <span>⧉</span>
            </div>
            <div className="cc-links">
              <a href={`${OPBNB_SCAN_URL}/token/${CORE_TOKEN_ADDRESS}`} target="_blank" rel="noopener noreferrer">Token ↗</a>
              <a href={`${OPBNB_SCAN_URL}/token/${CORE_TOKEN_ADDRESS}#code`} target="_blank" rel="noopener noreferrer">Code ↗</a>
            </div>
          </div>
          <div className="con-card">
            <div className="cc-head"><span>⛏️</span><h4>Mining Contract</h4></div>
            <div className="cc-addr" onClick={() => copyToClipboard(MINING_CONTRACT_ADDRESS)}>
              <code>{MINING_CONTRACT_ADDRESS.slice(0, 14)}...{MINING_CONTRACT_ADDRESS.slice(-10)}</code>
              <span>⧉</span>
            </div>
            <div className="cc-links">
              <a href={`${OPBNB_SCAN_URL}/address/${MINING_CONTRACT_ADDRESS}`} target="_blank" rel="noopener noreferrer">Contract ↗</a>
              <a href={`${OPBNB_SCAN_URL}/address/${MINING_CONTRACT_ADDRESS}#code`} target="_blank" rel="noopener noreferrer">Code ↗</a>
            </div>
          </div>
        </div>
      </section>

      {/* Community */}
      <section className="community-section">
        <div className="section-header">
          <span className="sh-icon">💬</span>
          <h2>JOIN OUR COMMUNITY</h2>
        </div>
        <div className="community-card">
          <div className="cc-content">
            <img src="/telegram.png" alt="Telegram" className="telegram-logo" />
            <div className="cc-text">
              <h3>Smartcore Official</h3>
              <p>Join our Telegram for updates, announcements & community discussions</p>
            </div>
          </div>
          <a href={TELEGRAM_CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="telegram-btn">
            <span>Join Channel</span>
            <span className="btn-arrow">→</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="coin-footer">
        <img src="/core.png" alt="CORE" className="cf-logo" />
        <p>CORE Token - Decentralized Mining Protocol</p>
        <small>© 2026 Smartcore. All rights reserved.</small>
        <small className="disclaimer">Crypto investments carry risk. DYOR.</small>
      </footer>
    </div>
  );
};

export default Coin;
